import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NzI18nService, tr_TR, en_US, zh_CN, ar_EG } from 'ng-zorro-antd/i18n';
import { BehaviorSubject, Observable } from 'rxjs';

export interface Language {
  code: string;
  name: string;
  nzLocale: any;
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly STORAGE_KEY = 'app_language';

  private currentLanguageSubject = new BehaviorSubject<string>('tr');
  public currentLanguage$: Observable<string> = this.currentLanguageSubject.asObservable();

  public availableLanguages: Language[] = [
    { code: 'tr', name: 'Türkçe', nzLocale: tr_TR },
    { code: 'en', name: 'English', nzLocale: en_US },
    { code: 'zh', name: '中文', nzLocale: zh_CN },
    { code: 'ar', name: 'العربية', nzLocale: ar_EG }
  ];

  constructor(
    private translate: TranslateService,
    private nzI18nService: NzI18nService
  ) {
    this.initLanguage();
  }

  /**
   * Başlangıç dilini ayarla
   */
  private initLanguage(): void {
    const savedLang = this.getSavedLanguage();
    const browserLang = this.translate.getBrowserLang();

    // Öncelik: Kaydedilmiş > Tarayıcı > Varsayılan (tr)
    let lang = savedLang || browserLang || 'tr';

    // Desteklenen diller arasında değilse varsayılanı kullan
    if (!this.availableLanguages.find(l => l.code === lang)) {
      lang = 'tr';
    }

    this.setLanguage(lang);
  }

  /**
   * Dili değiştir
   */
  setLanguage(langCode: string): void {
    const language = this.availableLanguages.find(l => l.code === langCode);

    if (!language) {
      console.error(`Language ${langCode} not found`);
      return;
    }

    // ngx-translate dilini değiştir
    this.translate.use(langCode);

    // ng-zorro i18n'i değiştir
    this.nzI18nService.setLocale(language.nzLocale);

    // LocalStorage'a kaydet
    this.saveLanguage(langCode);

    // Observable'ı güncelle
    this.currentLanguageSubject.next(langCode);
  }

  /**
   * Mevcut dili al
   */
  getCurrentLanguage(): string {
    return this.currentLanguageSubject.value;
  }

  /**
   * Kaydedilmiş dili localStorage'dan al
   */
  private getSavedLanguage(): string | null {
    return localStorage.getItem(this.STORAGE_KEY);
  }

  /**
   * Dili localStorage'a kaydet
   */
  private saveLanguage(langCode: string): void {
    localStorage.setItem(this.STORAGE_KEY, langCode);
  }

  /**
   * Çeviriyi al (shorthand)
   */
  instant(key: string, params?: any): string {
    return this.translate.instant(key, params);
  }

  /**
   * Async çeviriyi al
   */
  get(key: string, params?: any): Observable<string> {
    return this.translate.get(key, params);
  }
}
